import { AxiosInstance, AxiosResponse } from "axios";
import ZenDeskTicketModel, { UpdateZenDeskTicketModel } from "@/models/ZenDeskTicketModel";
import Shared from "@/common/shared";
import PagedResult from "@/models/PagedResult";
import TicketSearchingModel from "@/models/TicketSearchingModel";
import { RequesterModel } from "@/models/RequesterModel";
import { UserModel } from "@/models/UserModel";

export default class TicketService {
  private axiosInstance: AxiosInstance;

  public constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  public async getTickets(data: TicketSearchingModel): Promise<PagedResult<ZenDeskTicketModel>> {
    const url = `/Zendesk/list`;
    const res: AxiosResponse<PagedResult<ZenDeskTicketModel>> = await this.axiosInstance.post<
      PagedResult<ZenDeskTicketModel>
    >(url, data);
    return res.data || new PagedResult<ZenDeskTicketModel>();
  }

  public async getTicket(id: number): Promise<ZenDeskTicketModel> {
    const url = `/Zendesk/${id}`;
    const res: AxiosResponse<ZenDeskTicketModel> = await this.axiosInstance.get<ZenDeskTicketModel>(url);
    if (res.status === 404) {
      const conflictError = new Error();
      conflictError.name = Shared.notFound;
      conflictError.message = Shared.messages.TICKET_NOT_FOUND;
      return Promise.reject(conflictError);
    }
    return res.data;
  }

  public async createTicket(ticket: ZenDeskTicketModel): Promise<ZenDeskTicketModel> {
    const url = `/Zendesk`;
    const res: AxiosResponse<ZenDeskTicketModel> = await this.axiosInstance.post<ZenDeskTicketModel>(url, ticket);
    return res.data;
  }

  public async updateTicket(ticket: ZenDeskTicketModel | UpdateZenDeskTicketModel): Promise<ZenDeskTicketModel> {
    const url = `/Zendesk`;
    const res: AxiosResponse<ZenDeskTicketModel> = await this.axiosInstance.patch<ZenDeskTicketModel>(url, ticket);
    return res.data;
  }

  public async getRequesterByCompany(id: number): Promise<UserModel[]> {
    const url = `/Zendesk/user/${id}`;
    const res: AxiosResponse<UserModel[]> = await this.axiosInstance.get<UserModel[]>(url);
    return res.data || [];
  }

  public async updateTicketCompany(ticket: ZenDeskTicketModel): Promise<RequesterModel[]> {
    const url = `/Zendesk/upsert-ticket`;
    const res: AxiosResponse<RequesterModel[]> = await this.axiosInstance.post<RequesterModel[]>(url, {
      ticketId: ticket.id,
      companyId: ticket.companyId,
      requesterId: ticket.requesterId,
    });
    return res.data || [];
  }

  public async getAssignees(): Promise<RequesterModel[]> {
    const url = `/Zendesk/agent`;
    const res: AxiosResponse<RequesterModel[]> = await this.axiosInstance.get<RequesterModel[]>(url);
    return res.data || [];
  }
}
